import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Avatar,
  Rating,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocationOn,
  Verified,
  People,
  Assignment,
  Language,
  Phone,
  Email
} from '@mui/icons-material';
import './OrganisationCard.css';

const OrganisationCard = ({ organisation, variant = 'default' }) => {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    navigate(`/organisations/${organisation.id}`);
  };

  const handleContact = (e) => {
    e.stopPropagation();
    // TODO: Implement contact functionality
    console.log('Contact organisation:', organisation.id);
  };

  const handleWebsite = (e) => {
    e.stopPropagation();
    window.open(organisation.website, '_blank');
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Club':
        return 'primary';
      case 'Fédération':
        return 'secondary';
      case 'Salle de sport':
        return 'success';
      case 'Organisateur d\'événements':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatMembersCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  return (
    <Card 
      className={`organisation-card ${variant}`}
      onClick={handleViewProfile}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        }
      }}
    >
      {/* Cover Image */}
      <CardMedia
        component="img"
        height="160"
        image={organisation.coverImage}
        alt={organisation.name}
        className="organisation-cover"
      />

      {/* Logo positioned over cover */}
      <Box className="organisation-logo-container">
        <Avatar
          src={organisation.logo}
          alt={organisation.name}
          className="organisation-logo"
          sx={{ width: 60, height: 60, border: '3px solid white' }}
        >
          {organisation.name.charAt(0)}
        </Avatar>
        {organisation.verified && (
          <Tooltip title="Organisation vérifiée">
            <Verified 
              className="verified-badge" 
              sx={{ color: '#1976d2', fontSize: 20 }}
            />
          </Tooltip>
        )}
      </Box>

      <CardContent className="organisation-content" sx={{ flexGrow: 1, pt: 4 }}>
        {/* Header */}
        <Box className="organisation-header">
          <Typography 
            variant="h6" 
            component="h3" 
            className="organisation-name"
            sx={{ 
              fontWeight: 600,
              fontSize: '1.1rem',
              lineHeight: 1.3,
              mb: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {organisation.name}
          </Typography>

          <Chip 
            label={organisation.type}
            color={getTypeColor(organisation.type)}
            size="small"
            className="organisation-type"
            sx={{ mb: 1 }}
          />
        </Box>

        {/* Location */}
        <Box className="organisation-location" sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {organisation.location}
          </Typography>
        </Box>

        {/* Disciplines */}
        <Box className="organisation-disciplines" sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {organisation.disciplines.slice(0, 3).map((discipline, index) => (
              <Chip
                key={index}
                label={discipline}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: 24 }}
              />
            ))}
            {organisation.disciplines.length > 3 && (
              <Chip
                label={`+${organisation.disciplines.length - 3}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: 24 }}
              />
            )}
          </Box>
        </Box>

        {/* Description */}
        <Typography 
          variant="body2" 
          color="text.secondary"
          className="organisation-description"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            mb: 2
          }}
        >
          {organisation.description}
        </Typography>

        {/* Stats */}
        <Box className="organisation-stats" sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Rating 
              value={organisation.rating} 
              precision={0.1} 
              size="small" 
              readOnly 
              sx={{ mr: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary">
              ({organisation.reviewsCount})
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Membres">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <People sx={{ fontSize: 14, color: 'text.secondary', mr: 0.3 }} />
                <Typography variant="caption" color="text.secondary">
                  {formatMembersCount(organisation.membersCount)}
                </Typography>
              </Box>
            </Tooltip>
            
            <Tooltip title="Missions publiées">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment sx={{ fontSize: 14, color: 'text.secondary', mr: 0.3 }} />
                <Typography variant="caption" color="text.secondary">
                  {organisation.missionsPublished}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>

      <CardActions className="organisation-actions" sx={{ p: 2, pt: 0 }}>
        <Button 
          variant="contained" 
          fullWidth
          onClick={handleViewProfile}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2
          }}
        >
          Voir le profil
        </Button>
        
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          {organisation.website && (
            <Tooltip title="Site web">
              <IconButton 
                size="small" 
                onClick={handleWebsite}
                sx={{ color: 'text.secondary' }}
              >
                <Language fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {organisation.phone && (
            <Tooltip title="Téléphone">
              <IconButton 
                size="small" 
                onClick={handleContact}
                sx={{ color: 'text.secondary' }}
              >
                <Phone fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {organisation.email && (
            <Tooltip title="Email">
              <IconButton 
                size="small" 
                onClick={handleContact}
                sx={{ color: 'text.secondary' }}
              >
                <Email fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};

export default OrganisationCard;